'use strict';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import mapValues from 'lodash/mapValues';
import {LOAD} from 'redux-storage';
import {getSyncStatus, syncInProgress, SyncStatus} from '../../_libs/redux-observable-list';
import {GpsStatus} from '../../core-utilities/enums/GpsStatus';
import {gpsSupported} from '../../core-utilities-implementation/device-services/DeviceServicesCordova';
import {updatePosition} from '../../settings/actions/trackLocationActions';
import {reduxList} from '../redux/activeTimers';
import * as activeTimersActions from '../actions/activeTimersActions';
import {calcTotalTime, getLoc, isGpsItem, isLocCaptured} from '../utils';
import {getActiveTimersSafe} from './state';

export const _dependencies = {
  gpsSupported,
};

/**
 * @typedef {Object<String, ActiveTimersState~update>} ActiveTimersState~updates
 */

/**
 * @typedef {Object} ActiveTimersState~update
 * @property {SyncStatus} status
 * @property {Number} time
 */

/**
 * @typedef {ReduxObservableListState} ActiveTimersState
 * @property {ActiveTimersState~updates} updates
 */

export const initialState = reduxList.reducers.initialState;

export const getListId = reduxList.reducers.getListId;
const updatesReducer = reduxList.reducers.updatesReducer;

/**
 * @param {ActiveTimersState} state
 * @param {ReduxAction} action
 * @return {ActiveTimersState}
 */
export function activeTimers(state = reduxList.reducers.initialState, action) {
  switch (action.type) {
    case activeTimersActions.startTimer.TYPE:
      return startTimerReducer(state, action);

    case activeTimersActions.pauseTimer.TYPE:
      return pauseTimerReducer(state, action);

    case activeTimersActions.stopTimer.TYPE:
      return stopTimerReducer(state, action);

    case activeTimersActions.updateTimerGps.TYPE:
      return updatePositionReducer(state, action, true);

    case updatePosition.TYPE:
      return updatePositionReducer(state, action, false);

    case LOAD:
      return loadReducer(state, action);

    default:
      return reduxList.reducers.reducer(state, action);
  }
}

/**
 * @param {ActiveTimersState} state
 * @param {ReduxAction<StartTimerPayload>} action
 * @return {ActiveTimersState}
 */
function startTimerReducer(state, action) {
  /** @type {StartTimerPayload} */
  const payload = action.payload;
  const itemId = action.payload.requestId || action.payload.id
    ? getListId(action.payload.id, action.payload.requestId, state.list)
    : void 0;
  const existingItem = itemId
    ? state.list[itemId]
    : findByTarget(payload.targetCollection, payload.targetId)(state.list);
  if (!existingItem) {
    const syncStatus = getSyncStatus(state.updates[payload.requestId]);
    const newItem = {
      requestId: payload.requestId,
      target: {
        collection: payload.targetCollection,
        id: payload.targetId,
        value: payload.targetName,
      },
      parentName: payload.parentName,
      intervals: [
        {start: payload.startedAt},
      ],
      startedAt: payload.startedAt,
      endedAt: null,
      totalTime: 0,
      locCap: false,
      loc: [],
      syncStatus,
    };

    if (_dependencies.gpsSupported())
      newItem.gpsStatus = GpsStatus.REQUESTING;

    return {
      ...state,
      list: {
        ...state.list,
        [payload.requestId]: newItem,
      },
      updates: updatesReducer(state.updates, payload.requestId, syncStatus),
    };
  } else {
    const syncStatus = getSyncStatus(state.updates[itemId]);
    const newItem = {
      ...existingItem,
      intervals: [
        ...existingItem.intervals,
        {start: payload.startedAt},
      ],
      startedAt: payload.startedAt,
      endedAt: null,
      totalTime: calcTotalTime(existingItem.intervals),
      syncStatus,
    };

    if (_dependencies.gpsSupported())
      newItem.gpsStatus = GpsStatus.REQUESTING;

    return {
      ...state,
      list: {
        ...state.list,
        [itemId]: newItem,
      },
      updates: updatesReducer(state.updates, itemId, syncStatus),
    };
  }
}

/**
 * @param {ActiveTimersState} state
 * @param {ReduxAction<StopTimerPayload>} action
 * @return {ActiveTimersState}
 */
function pauseTimerReducer(state, action) {
  const itemId = getListId(action.payload.id, action.payload.requestId, state.list);
  const existingItem = state.list[itemId];
  if (existingItem) {
    return pauseImpl(state, action, itemId, existingItem, false);
  } else {
    // TODO: handle item already exists if needed
    console.warn(`pauseTimerReducer missing item`);
    return state;
  }
}

/**
 * @param {ActiveTimersState} state
 * @param {ReduxAction<StopTimerPayload>} action
 * @return {ActiveTimersState}
 */
function stopTimerReducer(state, action) {
  const itemId = getListId(action.payload.id, action.payload.requestId, state.list);
  const existingItem = state.list[itemId];
  if (existingItem) {
    const last = existingItem.intervals[existingItem.intervals.length - 1];
    const syncStatus = getSyncStatus(state.updates[itemId]);

    if (last.end) {
      // handle complete only

      return {
        ...state,
        list: {
          ...state.list,
          [itemId]: {
            ...existingItem,
            syncStatus,
            isStopped: true,
          },
        },
        updates: updatesReducer(state.updates, itemId, syncStatus),
      };

    } else {
      // apply endAt and complete

      return pauseImpl(state, action, itemId, existingItem, true);
    }

  } else {
    // TODO: handle item already exists if needed
    console.warn(`stopTimerReducer missing item`);
    return state;
  }
}

/**
 * @param {ActiveTimersState} state
 * @param {ReduxAction<StopTimerPayload>} action
 * @param {String} itemId
 * @param {Object} existingItem
 * @param {Boolean} isStopped
 * @return {ActiveTimersState}
 */
function pauseImpl(state, action, itemId, existingItem, isStopped) {
  /** @type {StopTimerPayload} */
  const payload = action.payload;
  const syncStatus = getSyncStatus(state.updates[itemId]);
  const intervals = [
    ...existingItem.intervals,
  ];
  const last = intervals[intervals.length - 1];
  intervals[intervals.length - 1] = {
    ...last,
    end: payload.endedAt,
  };

  const newItem = {
    ...existingItem,
    intervals,
    startedAt: null,
    endedAt: payload.endedAt,
    totalTime: calcTotalTime(intervals),
    syncStatus,
  };

  if (isStopped) {
    newItem.isStopped = true;
  }

  if (isGpsItem(newItem)) {
    newItem.gpsStatus = GpsStatus.STOPPED;
  }

  return {
    ...state,
    list: {
      ...state.list,
      [itemId]: newItem,
    },
    updates: updatesReducer(state.updates, itemId, syncStatus),
  };
}

/**
 * @param {ActiveTimersState} state
 * @param {ReduxAction<UpdatePositionPayload>} action
 * @param {Boolean} requestingOnly Update only items in the with GpsStatus.REQUESTING.
 * @return {ActiveTimersState}
 */
function updatePositionReducer(state, action, requestingOnly) {
  /** @type {UpdatePositionPayload} */
  const payload = action.payload;
  const updateLoc = getLocFromUpdate(action.payload);
  const updates = {};
  let hasPositionUpdates = false;

  const items = mapValues(state.list, (item, id) => {
    if (!isGpsItem(item) || (requestingOnly && item.gpsStatus !== GpsStatus.REQUESTING)) {
      return item;
    } else {
      hasPositionUpdates = true;
      const newLoc = getLoc(item.loc, updateLoc);
      const syncStatus = getSyncStatus(state.updates[id]);

      updates[id] = {
        status: syncStatus,
        // time: Date.now(),
      };

      return {
        ...item,
        loc: newLoc,
        gpsStatus: payload.gpsStatus,
        locCap: isLocCaptured(newLoc, item.locCap),
        syncStatus,
      };
    }
  });

  return !hasPositionUpdates ? state : {
    ...state,
    // items is already a complete mapping
    list: items,
    updates: {
      ...state.updates,
      ...updates,
    },
  };
}

/**
 * on redux-storage LOAD, merge in cached state, if it exists.
 * Reset all syncStatuses to pending.
 * @param {ActiveTimersState} state
 * @param {ReduxAction} action
 * @return {ActiveTimersState}
 */
function loadReducer(state, action) {
  const cachedState = getActiveTimersSafe(action.payload);

  if (cachedState) {
    if (cachedState.updates) {
      const list = {
        ...cachedState.list,
      };
      const updates = mapValues(cachedState.updates, (update, itemId) => {
        if (syncInProgress(update.status)) {
          if (list[itemId]) {
            const oldItem = list[itemId];
            list[itemId] = {
              ...oldItem,
              syncStatus: SyncStatus.PENDING,
            };
          }

          return {
            ...update,
            status: SyncStatus.PENDING,
          };
        } else {
          return update;
        }

      });
      return {
        ...state,
        ...cachedState,
        list: {
          ...state.list,
          ...list,
        },
        updates: {
          ...state.updates,
          ...updates,
        },
      };

    } else {
      return {
        ...state,
        ...cachedState,
        list: {
          ...state.list,
          ...cachedState.list,
        },
        updates: {
          ...state.updates,
          ...cachedState.updates,
        },
      };

    }

  } else {
    return state;
  }
}

/**
 * @param {String} targetCollection
 * @param {String} targetId
 * @param {Boolean} [includeStopped=false]
 * @return {function(*=)}
 */
export function findByTarget(targetCollection, targetId, includeStopped = false) {
  return list => find(list, (value, key) =>
    get(value, 'target.id') === targetId
    && get(value, 'target.collection') === targetCollection
    && (includeStopped || !get(value, 'isStopped')));
}

/**
 * @param {String} targetCollection
 * @param {String} targetId
 * @param {Object} list
 * @return {Array}
 */
export function findAllByTarget(targetCollection, targetId, list) {
  return filter(list, (value, key) =>
    get(value, 'target.id') === targetId
    && get(value, 'target.collection') === targetCollection);
}

export function hasGpsItems(state) {
  return filter(state.list, isGpsItem).length > 0;
}

/**
 * Convert the update payload in a gps entry.
 * @param {UpdatePositionPayload} payload
 * @return {?ActiveTimer#GPSEntry}
 */
export function getLocFromUpdate(payload) {
  /** @type {?ActiveTimer#GPSEntry} */
  let loc = {};

  switch (payload.gpsStatus) {
    case GpsStatus.WATCHING:
      // These 3 properties are required and this is basically an error since the geolocation impl has violated the spec contract
      if (payload.position && payload.position.coords) {
        loc.lat = payload.position.coords.latitude;
        loc.lon = payload.position.coords.longitude;
        loc.acc = payload.position.coords.accuracy;

        if (isNumber(payload.position.coords.altitude))
          loc.alt = payload.position.coords.altitude;

        if (isNumber(payload.position.coords.altitudeAccuracy))
          loc.altAcc = payload.position.coords.altitudeAccuracy;

        if (isNumber(payload.position.coords.heading))
          loc.head = payload.position.coords.heading;

        if (isNumber(payload.position.coords.speed))
          loc.speed = payload.position.coords.speed;
      }

      if (payload.location) {
        loc.val = payload.location;
      }

    /* fall-through */
    case GpsStatus.STOPPED:
    case GpsStatus.WATCHING_ERROR:
      loc.tim = payload.timestamp;
      break;

    case GpsStatus.ERROR:
    default:
      loc = null;
  }

  return loc;
}



// WEBPACK FOOTER //
// ./src/js/timers/reducers/activeTimersReducers.js